import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useApolloClient } from '@apollo/client';
import { MultiKey } from '../core/MultiKey';
import type { PortCostData, PortCostPair, MarcuraPortCosts } from './context';
import { MarcuraPortCostContext } from './context';
import { PortCostMap } from './portCostMap';
import { getStoredPortCosts, setStoredPortCost } from './store';
import { useAsRef } from '../../lib/util';
import { useNotify } from '../notifications/ToastNotifications';
import {
  GetMarcuraPortCostsDocument,
  type Assortment_AdminFragment,
  type GetMarcuraPortCostsMutation,
  type GetMarcuraPortCostsMutationVariables,
} from '../../graphql';
import { type ScenarioLocation } from '../../lib/planningData';
interface MarcuraPortCostProviderProps {
  children: React.ReactNode;
}

type PortCostResponse = { response: PortCostData };
type PortCostError = { portCostPair: PortCostPair; error: string };

interface PortCostsState {
  portCosts: MarcuraPortCosts;
  queue: PortCostPair[];
  responses: PortCostResponse[];
  errors: PortCostError[];
  loading: boolean;
  locationsById: Map<number, ScenarioLocation>;
  assortmentsById: Map<number, Assortment_AdminFragment>;
}

type PortCostsAction =
  | { type: 'PORT_COSTS_RESET'; portCosts: MarcuraPortCosts }
  | { type: 'QUEUE_PROCESSED' }
  | { type: 'ADDED_TO_QUEUE'; portCostPairs: PortCostPair[] }
  | { type: 'RESPONSES_RECEIVED'; responses: PortCostResponse[] }
  | { type: 'ERRORS_RECEIVED'; responses: PortCostError[] }
  | { type: 'LOADING_STARTED' }
  | { type: 'LOADING_ENDED' }
  | { type: 'SET_LOCATIONS'; locationsById: Map<number, ScenarioLocation> }
  | {
      type: 'SET_ASSORTMENTS';
      assortmentsById: Map<number, Assortment_AdminFragment>;
    };

export const initialPortCostMap = new PortCostMap(
  new MultiKey(2),
  new Map(),
  new Map(),
);
const initialState: PortCostsState = {
  portCosts: initialPortCostMap,
  queue: [],
  responses: [],
  errors: [],
  loading: false,
  locationsById: new Map(),
  assortmentsById: new Map(),
};

function getInitialState(): PortCostsState {
  return initialState;
}

const portCostsReducer = (
  state: PortCostsState,
  action: PortCostsAction,
): PortCostsState => {
  switch (action.type) {
    case 'PORT_COSTS_RESET': {
      return {
        ...state,
        portCosts: action.portCosts,
      };
    }
    case 'QUEUE_PROCESSED': {
      if (state.responses.length === 0 && state.errors.length === 0) {
        return state;
      }
      const newPortCosts = new PortCostMap(
        state.portCosts.map,
        state.portCosts.locationsById,
        state.portCosts.assortmentsById,
      );

      state.responses.forEach(({ response }) => newPortCosts.set(response));

      return {
        ...state,
        portCosts: newPortCosts,
        loading: false,
      };
    }
    case 'ADDED_TO_QUEUE': {
      const queue = state.queue;
      const queuedPairs = new Set(
        state.queue.map((pair) => `${pair[0]}_${pair[1]}`),
      );
      const responses = new Set(
        state.responses.map(
          (r) => `${r.response.location_id}_${r.response.assortment_id}`,
        ),
      );

      // Don't requeue already fetched or queued port costs
      const newPairs = action.portCostPairs.filter((pair) => {
        const isFetched = state.portCosts.get(...pair);
        const pairString = `${pair[0]}_${pair[1]}`;
        const isQueued = queuedPairs.has(pairString);
        const hasResponse = responses.has(pairString);

        return !isFetched && !isQueued && !hasResponse;
      });

      // Remove portCostPair from errors if it's requeued
      const newErrors = state.errors.filter(({ portCostPair }) => {
        const pairString = `${portCostPair[0]}_${portCostPair[1]}`;
        return !action.portCostPairs.some(
          (pair) => `${pair[0]}_${pair[1]}` === pairString,
        );
      });

      const newQueue = [...queue, ...newPairs];

      if (
        newPairs.length === 0 &&
        queue !== initialState.queue &&
        newErrors.length === state.errors.length
      ) {
        return state;
      }

      return {
        ...state,
        queue: newQueue,
        errors: newErrors,
      };
    }
    case 'RESPONSES_RECEIVED': {
      const responseKeys = new Set(
        action.responses.map(
          (res) => `${res.response.location_id}_${res.response.assortment_id}`,
        ),
      );

      const newQueue = state.queue.filter(
        (queueItem) => !responseKeys.has(`${queueItem[0]}_${queueItem[1]}`),
      );

      return {
        ...state,
        queue: newQueue,
        responses: [...state.responses, ...action.responses],
      };
    }
    case 'ERRORS_RECEIVED': {
      const errorKeys = new Set(
        action.responses.map(
          (err) => `${err.portCostPair[0]}_${err.portCostPair[1]}`,
        ),
      );

      const newQueue = state.queue.filter(
        (queueItem) => !errorKeys.has(`${queueItem[0]}_${queueItem[1]}`),
      );

      return {
        ...state,
        queue: newQueue,
        errors: [...state.errors, ...action.responses],
      };
    }
    case 'LOADING_STARTED': {
      return {
        ...state,
        loading: true,
      };
    }
    case 'LOADING_ENDED': {
      return {
        ...state,
        loading: false,
      };
    }
    case 'SET_LOCATIONS': {
      return {
        ...state,
        locationsById: action.locationsById,
      };
    }
    case 'SET_ASSORTMENTS': {
      return {
        ...state,
        assortmentsById: action.assortmentsById,
      };
    }
    default: {
      return state;
    }
  }
};

const summarizeErrors = (errors: PortCostError[]): string | null => {
  // Just show how many errors we have
  if (errors.length === 0) {
    return null;
  }

  return `Failed to load ${errors.length} port costs`;
};

const notifyPortCostLoadingToastId = 'PORTCOST_LOADING';

export const MarcuraPortCostProvider = ({
  children,
}: MarcuraPortCostProviderProps) => {
  const apolloClient = useApolloClient();
  const notify = useNotify({
    toastId: 'marcura-port-cost-provider',
    type: 'error',
  });

  const notifyLoading = useNotify({
    toastId: notifyPortCostLoadingToastId,
    dismissible: true,
  });

  const [state, dispatch] = useReducer(portCostsReducer, getInitialState());
  const {
    portCosts,
    queue,
    responses,
    errors,
    locationsById,
    assortmentsById,
    loading,
  } = state;

  // Store refs to state values so async useEffect callbacks can access the latest values
  const portCostsRef = useAsRef(portCosts);
  const queueRef = useAsRef(queue);

  // Initialize portCosts from indexedDB
  useEffect(() => {
    async function initialize() {
      const storedPortCosts = await getStoredPortCosts(
        portCostsRef,
        locationsById,
        assortmentsById,
      );
      dispatch({ type: 'PORT_COSTS_RESET', portCosts: storedPortCosts });
    }
    initialize();
  }, [portCostsRef, locationsById, assortmentsById]);

  // Save responses to indexedDB
  useEffect(() => {
    if (responses.length > 0) {
      responses.forEach(({ response }) => {
        setStoredPortCost(response);
      });
    }
  }, [responses]);

  // Process accumulated responses
  useEffect(() => {
    if (responses.length > 0 || errors.length > 0) {
      const error = summarizeErrors(errors);
      if (error) {
        notify(error);
      }
      dispatch({ type: 'QUEUE_PROCESSED' });
    }
  }, [responses, errors, notify]);

  // Update toast during loading and add new port costs once whole queue is processed
  useEffect(() => {
    const loading = queue.length > 0;
    const ready = queue.length === 0 && queue !== initialState.queue;

    if (loading) {
      let message = `Loading Marcura port costs, ${queue.length} remaining`;
      if (errors.length > 0) {
        message += `, ${errors.length} errors`;
      }

      notifyLoading(message, { type: 'info', isLoading: true });
    } else if (ready) {
      if (errors.length > 0) {
        notifyLoading(`Failed to load ${errors.length} port costs`, {
          type: 'error',
        });
      } else {
        notifyLoading('Port costs loaded', {
          type: 'success',
          updateOnly: true,
          isLoading: false,
        });
      }
    }
  }, [notifyLoading, responses, queue.length, errors, queue]);

  // Fetch queued port costs
  useEffect(() => {
    if (loading) {
      return;
    }
    const fetcher = async () => {
      const currentQueue = queueRef.current;

      if (currentQueue.length === 0) {
        return;
      }

      try {
        dispatch({ type: 'LOADING_STARTED' });

        const batchSize = 200;
        const totalBatches = Math.ceil(currentQueue.length / batchSize);

        // Process batches sequentially
        for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
          const startIdx = batchIndex * batchSize;
          const endIdx = Math.min(startIdx + batchSize, currentQueue.length);
          const currentBatch = currentQueue.slice(startIdx, endIdx);

          try {
            const { data } = await apolloClient.mutate<
              GetMarcuraPortCostsMutation,
              GetMarcuraPortCostsMutationVariables
            >({
              mutation: GetMarcuraPortCostsDocument,
              variables: {
                input: currentBatch.map(([locationId, assortmentId]) => ({
                  locationId,
                  assortmentId,
                })),
              },
            });

            if (data?.getMarcuraPortCosts?.length) {
              // Process each result in the batch
              const responses: PortCostResponse[] = [];
              data.getMarcuraPortCosts.forEach((apiResponse) => {
                if (apiResponse) {
                  // Transform the GraphQL response to match our expected PortCostData structure
                  const portCostData: PortCostData = {
                    id: apiResponse.id,
                    marcura_port_id: apiResponse.marcura_port_id,
                    marcura_group_id: apiResponse.marcura_group_id,
                    location_id: apiResponse.location_id,
                    assortment_id: apiResponse.assortment_id,
                    data: {
                      costs: Array.isArray(apiResponse.data?.costs)
                        ? apiResponse.data.costs
                            .filter(
                              (cost): cost is NonNullable<typeof cost> =>
                                cost !== null,
                            )
                            .map((cost) => ({
                              ...cost,
                              dwtMax: cost?.dwtMax ?? 0,
                              dwtMin: cost?.dwtMin ?? 0,
                            }))
                        : [],
                    },
                  };

                  responses.push({
                    response: portCostData,
                  });
                }
              });

              dispatch({
                type: 'RESPONSES_RECEIVED',
                responses,
              });
            } else if (data?.getMarcuraPortCosts?.length === 0) {
              const responses: PortCostResponse[] = [];
              currentBatch.forEach(([location_id, assortment_id]) => {
                responses.push({
                  response: {
                    id: 0,
                    marcura_port_id: '',
                    marcura_group_id: '',
                    location_id,
                    assortment_id,
                    data: { costs: [] },
                  },
                });
              });
              dispatch({ type: 'RESPONSES_RECEIVED', responses });
            } else {
              throw new Error('Port costs data not found');
            }
          } catch (error) {
            const batchErrors: PortCostError[] = currentBatch.map(
              (portCostPair) => ({
                portCostPair,
                error: error instanceof Error ? error.message : String(error),
              }),
            );

            dispatch({
              type: 'ERRORS_RECEIVED',
              responses: batchErrors,
            });
          }
        }
      } catch (error) {
        console.error('Error fetching port costs:', error);
        notifyLoading('Error fetching port costs');
      } finally {
        dispatch({ type: 'LOADING_ENDED' });
      }
    };

    if (queue.length > 0) {
      void fetcher();
    }
  }, [
    apolloClient,
    notifyLoading,
    queue,
    queueRef,
    loading,
    assortmentsById,
    locationsById,
  ]);

  const queuePortCostsForPairs = useCallback(
    (portCostPairs: PortCostPair[]) => {
      if (portCostPairs.length === 0) return;
      dispatch({ type: 'ADDED_TO_QUEUE', portCostPairs });
    },
    [],
  );

  const setLocations = useCallback(
    (locationsById: Map<number, ScenarioLocation>) => {
      dispatch({ type: 'SET_LOCATIONS', locationsById });
    },
    [dispatch],
  );

  const setAssortments = useCallback(
    (assortmentsById: Map<number, Assortment_AdminFragment>) => {
      dispatch({ type: 'SET_ASSORTMENTS', assortmentsById });
    },
    [dispatch],
  );

  const fetchMissingMarcuraPortCosts = useCallback(
    async (
      existingPortCosts: MarcuraPortCosts,
      portIds: Set<number>,
      assortmentIds: Set<number>,
    ): Promise<MarcuraPortCosts> => {
      const missingPairs: PortCostPair[] = [];

      // Generate all possible combinations of portIds and assortmentGroupIds
      portIds.forEach((portId) => {
        assortmentIds.forEach((assortmentId) => {
          // Skip if we already have this port cost
          if (!existingPortCosts.get(portId, assortmentId)) {
            missingPairs.push([portId, assortmentId]);
          }
        });
      });

      if (missingPairs.length > 0) {
        notify('Fetching missing port costs');
        queuePortCostsForPairs(missingPairs);
      }

      return existingPortCosts;
    },
    [queuePortCostsForPairs, notify],
  );

  const resetPortCosts = useCallback(() => {
    dispatch({ type: 'PORT_COSTS_RESET', portCosts: initialPortCostMap });
  }, []);

  // Modify the context value to include the new method
  const contextValue = useMemo(
    () => ({
      portCosts,
      queuePortCostsForPairs,
      fetchMissingMarcuraPortCosts,
      resetPortCosts,
      setLocations,
      setAssortments,
    }),
    [
      portCosts,
      queuePortCostsForPairs,
      fetchMissingMarcuraPortCosts,
      resetPortCosts,
      setLocations,
      setAssortments,
    ],
  );

  return (
    <MarcuraPortCostContext.Provider value={contextValue}>
      {children}
    </MarcuraPortCostContext.Provider>
  );
};
